<template>
  <div class="header-wrapper txt-center">
    <div class="h1">{{ t('users.pageTitle') }}</div>
  </div>

  <!--FILTERS BLOCK-->
  <div class="filters-list flex border-filters">
    <ul class="filters-nav flex grow js-list">
      <li
          v-for="roleFilter in roleFilters"
          :key="roleFilter.value"
          :class="['filters-nav-item', 'js-item', { active: selectedRole === roleFilter.value }]"
          @click="selectedRole = roleFilter.value"
      >
        {{ t(roleFilter.labelKey) }}<span class="down-mark"></span>
      </li>
    </ul>
    <div class="filters-search">
      <div class="form-item">
        <input
            v-model="searchQuery"
            class="input-slim"
            type="search"
            name="search"
            :placeholder="t('users.searchPlaceholder')"
        />
        <button class="btn-square btn-search" type="submit">
          <svg class="svg-search">
            <use href="/img/sprite.svg#svg-search"></use>
          </svg>
        </button>
      </div>
    </div>
    <div class="filters-button">
      <router-link to="/user/add/" class="btn btn-slim">
        <svg class="svg-plus">
          <use href="/img/sprite.svg#svg-plus"></use>
        </svg>
        <span>{{ t('users.addUser') }}</span>
      </router-link>
    </div>
  </div>

  <div v-if="usersLoading" class="table-group">
    <div class="table-loader"></div>
  </div>

  <div v-if="usersError" class="mc-error">{{ usersError }}</div>

  <div v-if="usersLoaded" class="table-wrapper">
    <table class="table user-table">
      <thead class="table-thead">
      <tr>
        <th
            v-for="column in tableColumns"
            :key="column.key"
            :class="column.class"
        >
          <template v-if="column.sortKey">
            <button class="sort-btn" type="button" @click="setSort(column.sortKey)">
              {{ t(column.labelKey) }}
              <span class="sort-icons" aria-hidden="true">
                <span :class="['sort-up', { active: sortKey === column.sortKey && sortDir === 'asc' }]"></span>
                <span :class="['sort-down', { active: sortKey === column.sortKey && sortDir === 'desc' }]"></span>
              </span>
            </button>
          </template>
        </th>
      </tr>
      </thead>
      <tbody class="table-tbody">
      <tr v-for="(user, index) in filteredUsers" :key="user.login || index">
        <td class="cell-user"><router-link :to="{ name: 'user', params: { id: user.id } }">{{ user.fio }}</router-link></td>
        <td class="cell-company">{{ user.company || '-' }}</td>
        <td class="cell-login">{{ user.login }}</td>
        <td class="cell-email">{{ user.email }}</td>
        <td class="cell-role">{{ user.work?.UF_NAME || '-' }}</td>
        <td class="cell-options">
          <router-link :to="{ name: 'user-edit', params: { id: user.id } }" class="btn-edit has-tooltip" type="button">
            <span class="btn-tooltip">{{ t('users.tooltip.edit') }}</span>
          </router-link>
        </td>
        <td class="cell-options">
          <button class="btn-locked has-tooltip" type="button">
            <span class="btn-tooltip">{{ t('users.tooltip.block') }}</span>
          </button>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { useI18n } from 'vue-i18n';
import { sortArray } from '@/utils/sortUtils';

export default {
  name: 'Users',
  setup() {
    const { t, locale } = useI18n();

    return {
      t,
      locale,
    };
  },
  data: () => ({
    sortKey: 'fio',
    sortDir: 'asc',
    selectedRole: 'all',
    searchQuery: '',
  }),
  created() {
    if (this.profile.role !== 'admin')
      this.$router.push('/');
    this.fetchUsers();
  },
  computed: {
    ...mapGetters('users', [
      'users',
      'usersLoading',
      'usersError',
      'usersLoaded',
    ]),
    ...mapGetters({
      profile: 'user'
    }),
    roleFilters() {
      return [
        { value: 'all', labelKey: 'users.filters.all' },
        { value: 'admin', labelKey: 'users.filters.directors' },
        { value: 'manager', labelKey: 'users.filters.managers' },
        { value: 'supplier', labelKey: 'users.filters.suppliers' },
      ];
    },

    tableColumns() {
      return [
        { key: 'fio', class: 'cell-user', sortKey: 'fio', labelKey: 'users.table.fio' },
        { key: 'company', class: 'cell-company', sortKey: 'company', labelKey: 'users.table.company' },
        { key: 'login', class: 'cell-login', sortKey: 'login', labelKey: 'users.table.login' },
        { key: 'email', class: 'cell-email', sortKey: 'email', labelKey: 'users.table.email' },
        { key: 'work', class: 'cell-role', sortKey: 'work', labelKey: 'users.table.role' },
        { key: 'edit', class: 'cell-options' },
        { key: 'block', class: 'cell-options' },
      ];
    },

    sortedUsers() {
      return sortArray(this.users || [], this.sortKey, this.sortDir, (user, key) => {
        if (key === 'work') {
          return user.work?.UF_NAME || '';
        }
        return user[key];
      });
    },

    filteredUsers() {
      const search = this.normalizeSearch(this.searchQuery);

      return this.sortedUsers.filter(user => {
        // Фильтр по роли
        const matchRole = this.selectedRole === 'all' || user.work?.UF_CODE === this.selectedRole;

        // Поиск по ФИО, логину, email
        const matchSearch = !search || [
          user.fio,
          user.login,
          user.email,
          user.company,
          user.work.UF_NAME,
        ].some(field => this.normalizeSearch(field).includes(search));

        return matchRole && matchSearch;
      });
    },
  },
  methods: {
    ...mapActions('users', [
      'fetchUsers',
    ]),

    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        return;
      }

      this.sortKey = key;
      this.sortDir = 'asc';
    },

    normalizeSearch(value) {
      return String(value ?? '').trim().toLowerCase();
    },
  },
};
</script>